import { useEffect, useMemo } from "react";

import { TicketAttachmentMeta } from "@signalist/shared-types";

import { useTicketAttachmentSource } from "./ticketAttachmentSource";

/**
 * An object URL for one attachment's bytes, for inline thumbnails (PREC-3938 decision 1). The
 * download route needs the JWT, so an `<img src>` cannot point at it directly; the blob comes
 * through RTK Query and the URL is revoked when the component unmounts or the blob changes. The
 * blob hook comes from `TicketAttachmentSourceContext`, so the same thumbnail works on the staff
 * page (PREC-3940).
 */
export const useTicketAttachmentUrl = (
	attachment: TicketAttachmentMeta,
	enabled: boolean
): string | null => {
	const { useBlobQuery } = useTicketAttachmentSource();
	const { data: blob } = useBlobQuery(
		{ ticketId: attachment.ticketId, attachmentId: attachment.id },
		{ skip: !enabled }
	);
	const url = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);

	useEffect(
		() => () => {
			if (url) {
				URL.revokeObjectURL(url);
			}
		},
		[url]
	);

	return url;
};
